import { styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { DatePicker as DatePickerMUI } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { RowBox } from '../../styles/commonStyles';
import { theme } from '../../configs/theme';
import ConditionalWrapper from '../wrappers/ConditionalWrapper';
import { TextWrapper, TextWrapperProps } from './TextWrapper';
import { toDateOnly } from '../../utils/dates';

/**
 * Props for the DatePicker component.
 */
type DatePickerProps = {
  /**
   * Callback function triggered when the date changes.
   * Expects a synthetic event-like object with target.name and target.value, where value is
   * "YYYY-MM-DD", or an empty string when cleared.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (event: any) => void;

  /**
   * The name of the input field, used to identify the field.
   */
  name?: string;

  /**
   * The current value of the date picker, formatted as "YYYY-MM-DD" or null/empty. A timestamp is
   * shown as its local calendar day.
   */
  value?: string | null;

  /**
   * Position of the icon in the input field.
   * Options: `'start' | 'end'`. Defaults to `'start'`.
   */
  iconPosition?: 'start' | 'end';

  /**
   * Is there an error in the input field?
   */
  error?: boolean;

  /**
   * Text to display below if error.
   */
  errorText?: string;

  datePickerView?: ('year' | 'month' | 'day')[];

  minDate?: string;
  maxDate?: string;
  shouldDisableDate?: (date: Dayjs) => boolean;
  disabled?: boolean;

  /**
   * If true, displays a red asterisk (*) next to the label to indicate a required field.
   */
  required?: boolean;

  wrapperProps?: Omit<TextWrapperProps, 'children'>;
};

/**
 * A styled DatePicker component using MUI that allows users to select a date.
 * We parse empty/invalid values as null => shows blank field instead of defaulting to "today."
 */

export const DatePicker = ({
  onChange,
  name,
  value = '',
  iconPosition = 'start',
  datePickerView = ['year', 'month', 'day'],
  minDate,
  maxDate,
  error,
  errorText,
  shouldDisableDate,
  disabled,
  required,
  wrapperProps,
}: DatePickerProps) => {
  /**
   * Parses a date or a timestamp into the local calendar day, or null if invalid/empty.
   */
  const parseDate = (dateValue: string | null | undefined) => {
    const dateOnly = toDateOnly(dateValue);
    return dateOnly ? dayjs(dateOnly) : null;
  };

  const [internalValue, setInternalValue] = useState<Dayjs | null>(parseDate(value));

  // A date typed halfway is sent on as empty, so the form has no date while the field keeps the
  // text. Parsing that empty value back would wipe the field mid-typing.
  useEffect(() => {
    setInternalValue((current) =>
      !value && current && !current.isValid() ? current : parseDate(value)
    );
  }, [value]);

  /**
   * Handles the change event when a new date is selected or cleared.
   */
  const handleChange = (date: Dayjs | null) => {
    setInternalValue(date); // Store the actual selected date

    // Return empty string instead of null to avoid controlled/uncontrolled input warnings
    const formattedValue = date?.isValid() ? date.format('YYYY-MM-DD') : '';

    if (onChange) {
      onChange({
        target: {
          name,
          value: formattedValue,
        },
      });
    }
  };

  return (
    <ConditionalWrapper
      condition={!!wrapperProps}
      wrapper={(children) => (
        <TextWrapper sx={{ width: '100%' }} {...wrapperProps} required={required}>
          {children}
        </TextWrapper>
      )}
    >
      <RowBox sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <DatePickerStyled
          disabled={disabled}
          onChange={handleChange}
          value={internalValue}
          views={datePickerView}
          minDate={minDate ? (parseDate(minDate) as Dayjs) : undefined}
          maxDate={maxDate ? (parseDate(maxDate) as Dayjs) : undefined}
          shouldDisableDate={shouldDisableDate}
          slotProps={{
            actionBar: {
              actions: ['clear', 'cancel', 'accept'],
            },
            inputAdornment: {
              position: 'end',
              style: { margin: '0px' },
            },
            textField: {
              inputProps: { placeholder: '' },
              style: {
                padding: '0px',
                margin: '0px',
                border: error ? `1px solid ${theme.palette.status.rejected}` : '1px solid grey',
                borderRadius: '8px',
                backgroundColor: theme.palette.customColors.inputBackground,
              },
            },
          }}
        />
        {error && (
          <p style={{ color: theme.palette.status.rejected, fontSize: '12px', margin: 0 }}>
            {errorText}
          </p>
        )}
      </RowBox>
    </ConditionalWrapper>
  );
};

const DatePickerStyled = styled(DatePickerMUI)(({ theme }) => ({
  width: '100%',
  background: theme.palette.customColors.inputBackground,
  borderRadius: '8px',
  '.MuiInputBase-input': {
    padding: '12px 3px 12px 10px',
  },
  '.MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    padding: '0px',
  },
}));
